import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { AirMicrobialSampleSet } from "@/services/backend/samples/types/air-microbial-sample";
import { Filter } from "@/shared/lib/filter/filter";
import { BaseSamplePatchRequest } from "@/services/backend/samples/types/sample";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesAirMicrobialApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesAirMicrobialSampleSet"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      samplesAirMicrobialSampleSetPut: build.mutation<
        AirMicrobialSampleSet,
        PutRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesAirMicrobialSampleSet", id: request.id },
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesAirMicrobialSampleSetUpdateLab: build.mutation<
        AirMicrobialSampleSet,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesAirMicrobialSampleSet", id: request.id },
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesAirMicrobialSampleSetShow: build.query<
        AirMicrobialSampleSet,
        ShowRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("SamplesAirMicrobialSampleSet"),
      }),
      samplesAirMicrobialSampleSetList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/air-microbial/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.sampleSets, "SamplesAirMicrobialSampleSet"),
      }),
      samplesAirMicrobialSampleSetDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: [
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
    }),
  });

export const {
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetUpdateLabMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
  useSamplesAirMicrobialSampleSetListQuery,
  useLazySamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetDeleteMutation,
} = samplesAirMicrobialApi;

export type PutRequest = AirMicrobialSampleSet;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

export interface AirMicrobialSamplePatchRequest extends BaseSamplePatchRequest {
  samplingVolume?: number;
  deviceId?: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  sampleSets: AirMicrobialSampleSet[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached air microbial sample set for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateAirMicrobialSampleSetCache = updateCache(
  samplesAirMicrobialApi,
  "samplesAirMicrobialSampleSetShow",
);
